'use client';

import { createContext, useState, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, PaletteMode } from '@mui/material/styles';
import { CssBaseline, useMediaQuery } from '@mui/material';
import designTokens from '@/theme/theme.json';

type Mode = 'light' | 'dark' | 'system';

interface ThemeContextProps {
  mode: Mode;
  setMode: (mode: Mode) => void;
  resolvedMode: PaletteMode;
}

export const ThemeContext = createContext<ThemeContextProps>({
  mode: 'system',
  setMode: () => {},
  resolvedMode: 'light',
});

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>('system');
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  
  const resolvedMode = useMemo(() => {
    if (mode === 'system') return prefersDark ? 'dark' : 'light';
    return mode;
  }, [mode, prefersDark]);

  const theme = useMemo(() => {
    const palette     = designTokens.colorSchemes[resolvedMode].palette;
    const components  = designTokens.components;
    const typography  = designTokens.typography;
  
    return createTheme({
      cssVariables: true,
      palette: { mode: resolvedMode, ...palette },
      typography,
      components: {
        ...components,
        MuiButton: {
          defaultProps: {
            ...components.MuiButton?.defaultProps,
            disableRipple: true, // Disables the ripple effect on all buttons as it does not do it correctly and we do not apply it in our style.
          },
          styleOverrides: {
            ...components.MuiButton?.styleOverrides,
            root: {
              ...components.MuiButton?.styleOverrides?.root,
              textTransform: 'none', // Disable the uppercase on all buttons as it does not disable it correctly and we do not apply it in our style.
            }
          }
        },
        MuiIconButton: {
          defaultProps: {
            ...components.MuiIconButton?.defaultProps,
            disableRipple: true
          },
          styleOverrides: {
            ...components.MuiIconButton?.styleOverrides
          }
        },
      }
    });
  }, [resolvedMode]);  

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolvedMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}